// Utility functions for managing game state using localStorage
const GAME_STATE_PREFIX = "wordyverse_game_";
const TIMEZONE = "America/Los_Angeles"; // Pacific Time

/**
 * Generates a unique key for the game state in localStorage
 * @param {string} dateStr - The date of the game
 * @param {string|null} publicationId - Optional publication ID
 * @returns {string} - The localStorage key
 */
const getGameStateKey = (dateStr, publicationId = null) => {
  const pubSuffix = publicationId ? `_${publicationId}` : "";
  return `${GAME_STATE_PREFIX}${dateStr}${pubSuffix}`;
};

/**
 * Get the current date in Pacific Time as a formatted string (YYYY-MM-DD)
 * @returns {string} Formatted date string
 */
const getTodayInPTString = () => {
  // Create a date object for "now"
  const now = new Date();

  // Format it in Pacific Time
  return now
    .toLocaleString("en-US", {
      timeZone: TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .split("/")
    .reverse()
    .join("-"); // Convert MM/DD/YYYY to YYYY-MM-DD
};

/**
 * Format date as YYYY-MM-DD
 * @param {Date} date - The date to format
 * @returns {string} - Formatted date string
 */
export const formatDateForStorage = (date) => {
  if (!date) return null;

  // Format date in PT timezone
  const ptDateString = date.toLocaleString("en-US", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // Convert MM/DD/YYYY to YYYY-MM-DD
  const [month, day, year] = ptDateString.split("/");
  return `${year}-${month}-${day}`;
};

/**
 * Saves the current game state to localStorage
 * @param {string} dateStr - The date of the game in string format (YYYY-MM-DD)
 * @param {Array} guesses - Array of guess strings
 * @param {Object} wordData - Data about the current word
 * @param {boolean} isGameOver - Whether the game is finished
 * @param {string|null} publicationId - Optional publication ID
 * @returns {void}
 */
export const saveGameState = (
  dateStr,
  guesses,
  wordData,
  isGameOver,
  publicationId = null
) => {
  if (!dateStr || !guesses || !wordData) return;

  const stateKey = getGameStateKey(dateStr, publicationId);

  const gameState = {
    dateStr,
    guesses,
    word: wordData.word,
    theme: wordData.theme || null,
    isGameOver,
    lastPlayed: new Date().toISOString(),
    publicationId: publicationId || null,
  };

  try {
    localStorage.setItem(stateKey, JSON.stringify(gameState));
  } catch (error) {
    console.error("Error saving game state:", error);
  }
};

/**
 * Gets the saved game state from localStorage
 * @param {string} dateStr - The date of the game in string format
 * @param {string|null} publicationId - Optional publication ID
 * @returns {Object|null} - The saved game state or null if not found
 */
export const getGameState = (dateStr, publicationId = null) => {
  if (!dateStr) return null;

  const stateKey = getGameStateKey(dateStr, publicationId);
  const savedState = localStorage.getItem(stateKey);

  if (!savedState) return null;

  try {
    return JSON.parse(savedState);
  } catch (error) {
    console.error("Error parsing saved game state:", error);
    return null;
  }
};

/**
 * Clears the saved game state for a specific date
 * @param {string} dateStr - The date of the game in string format
 * @param {string|null} publicationId - Optional publication ID
 * @returns {void}
 */
export const clearGameState = (dateStr, publicationId = null) => {
  if (!dateStr) return;

  const stateKey = getGameStateKey(dateStr, publicationId);
  localStorage.removeItem(stateKey);
};

/**
 * Checks if the saved game state is for today in PT
 * @param {string} dateStr - The date to check in YYYY-MM-DD format
 * @returns {boolean} - True if the date is today, false otherwise
 */
export const isCurrentDay = (dateStr) => {
  if (!dateStr) return false;

  const todayStr = getTodayInPTString();
  return dateStr === todayStr;
};

/**
 * Gets all saved game states for a publication
 * @param {string|null} publicationId - Optional publication ID
 * @returns {Array} - Array of saved game states
 */
export const getAllGameStates = (publicationId = null) => {
  const states = [];
  const prefix = publicationId
    ? `${GAME_STATE_PREFIX}_${publicationId}_`
    : GAME_STATE_PREFIX;

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(prefix)) {
        const state = localStorage.getItem(key);
        if (state) {
          states.push(JSON.parse(state));
        }
      }
    }
  } catch (error) {
    console.error("Error retrieving game states:", error);
  }

  return states;
};
